// The phone app's pages (docs/mobile-pwa-plan.md "Pages on the phone").
//
// `key` is the name jobq-api's GET /features `mobile.pages` uses (mobile_app.py) to switch a
// page on for phones. `built` = this UI has a phone screen for it; a page that is switched on
// but not built (or built but not switched on) shows today's screen. `admin` = the route asks
// for the admin PIN today (App.jsx ProtectedRoute); the phone keeps that exactly.
//
// Job Entry (/job-entry, /jobs/new) is NOT a phone page (Mike, 2026-10-08): the phone menus never offer it, and
// opening it shows the desktop page. Shipping was left off with it until 2026-10-29 (Mike: "a full featured mobile
// optimized version"): the phone Shipping hub, screens/ShippingScreen.jsx.

export const PHONE_PAGES = Object.freeze([
  // Shell
  { key: 'home', label: 'Home', icon: 'fa-home', path: '/', group: 'shell', built: true },
  // Phase 1: shop operators
  { key: 'queue', label: 'Job Queue', icon: 'fa-layer-group', path: '/queue', group: 'shop', built: true },
  { key: 'jobs', label: 'Jobs', icon: 'fa-clipboard-list', path: '/jobs', group: 'shop', built: true },
  { key: 'receiving', label: 'Receiving', icon: 'fa-box-open', path: '/receiving', group: 'shop', built: true },
  // The Shipping hub (2026-10-29): not admin, as on the desktop (the Working-as PIN names who did what).
  { key: 'shipping', label: 'Shipping', icon: 'fa-truck', path: '/shipping-queue', group: 'shop', built: true },
  { key: 'consumables', label: 'Consumables', icon: 'fa-toolbox', path: '/consumables', group: 'shop', admin: true, built: true },
  { key: 'beacons', label: 'Beacons', icon: 'fa-broadcast-tower', path: '/beacons', group: 'shop', admin: true, feature: 'palletBeacons', built: true },
  { key: 'cameras', label: 'Cameras', icon: 'fa-video', path: '/cameras', group: 'shop', admin: true, built: true },
  // The compressor shed's temperature (2026-10-26). Not admin: the alert pushes link here. Read only on the
  // phone (settings stay on the desktop page); the phone shell only once jobq-api mobile_app.py lists it.
  { key: 'sensors', label: 'Shop sensors', icon: 'fa-thermometer-half', path: '/sensors', group: 'shop', built: true },
  // Any employee's own I'm here / I'm leaving for the door alarm (2026-10-25). Not admin. One page for
  // desktop, kiosk and phone (pages/CheckInPage.jsx); the phone shell only once jobq-api switches it on.
  { key: 'check-in', label: 'Check in', icon: 'fa-door-open', path: '/check-in', group: 'shop', built: true },
  // The in-app scanner (also a bottom tab): last on the Home / More lists.
  { key: 'scan', label: 'Scan', icon: 'fa-qrcode', path: '/scan', group: 'shop', built: true },
  // Where a sales-order pallet beacon's QR lands (2026-10-23). Not a menu page (group 'scanned': never in
  // the More panel / Home); it rides with Scan, so it is a phone page wherever Scan is (`rides`).
  { key: 'pallet-check', label: 'Pallet check', icon: 'fa-pallet', path: '/pallet-check', group: 'scanned', rides: 'scan', built: true },
  // Phase 2: Mike and Jesse
  { key: 'dashboard', label: 'Dashboard', icon: 'fa-tachometer-alt', path: '/dashboard', group: 'office', built: true },
  { key: 'progress', label: 'Progress', icon: 'fa-tasks', path: '/progress', group: 'office', built: true },
  { key: 'scheduling', label: 'Scheduling', icon: 'fa-calendar-alt', path: '/scheduling', group: 'office', built: true },
  { key: 'sales-orders', label: 'Sales Orders', icon: 'fa-file-invoice-dollar', path: '/sales-orders', group: 'office', admin: true, built: true },
  { key: 'customers', label: 'Customers', icon: 'fa-users', path: '/customers', group: 'office', admin: true, built: true },
  { key: 'vendors', label: 'Vendors', icon: 'fa-truck-loading', path: '/vendors', group: 'office', admin: true, built: true },
  { key: 'purchase-orders', label: 'Purchase Orders', icon: 'fa-shopping-cart', path: '/purchase-orders', group: 'office', admin: true, built: true },
  { key: 'invoices', label: 'Invoices', icon: 'fa-receipt', path: '/invoices', group: 'office', admin: true, built: true },
  { key: 'finance', label: 'Finance', icon: 'fa-university', path: '/finance', group: 'office', admin: true, built: true },
  { key: 'parts', label: 'Parts', icon: 'fa-cog', path: '/parts', group: 'office', admin: true, built: true },
  { key: 'materials', label: 'Materials', icon: 'fa-cubes', path: '/materials-entry', group: 'office', admin: true, built: true },
  { key: 'user-management', label: 'User Management', icon: 'fa-user-cog', path: '/user-management', group: 'office', admin: true, built: true },
].map((p) => Object.freeze({ built: false, admin: false, feature: null, rides: null, ...p })));

export const PAGES_BY_KEY = Object.freeze(Object.fromEntries(PHONE_PAGES.map((p) => [p.key, p])));

/** Keys of the pages this build has a phone screen for. */
export const BUILT_PAGE_KEYS = Object.freeze(PHONE_PAGES.filter((p) => p.built).map((p) => p.key));

// Paths under a phone page's path that belong to something else.
const NOT_A_PHONE_PAGE = ['/jobs/new'];

/** The phone page a path belongs to ('/jobs/812' -> 'jobs'), or null (not a phone page). */
export function pageKeyForPath(pathname) {
  const path = String(pathname || '/').replace(/\/+$/, '') || '/';
  if (NOT_A_PHONE_PAGE.some((p) => path === p || path.startsWith(`${p}/`))) return null;
  if (path === '/') return 'home';
  const hit = PHONE_PAGES.find((p) => p.path !== '/' && (path === p.path || path.startsWith(`${p.path}/`)));
  return hit ? hit.key : null;
}

// Bottom tabs (plan section 4). `to` is a page key; More opens the panel.
export const OPERATOR_TABS = Object.freeze(['queue', 'jobs', 'scan', 'receiving', 'more']);
export const ADMIN_TABS = Object.freeze(['home', 'sales-orders', 'queue', 'scan', 'more']);

/** The pages the More panel lists, grouped, in plan order (features gate a page's presence). */
export function morePanelGroups(features) {
  const shown = (p) => !p.feature || Boolean(features && features[p.feature] && features[p.feature].enabled);
  return [
    { title: 'Shop floor', pages: PHONE_PAGES.filter((p) => p.group === 'shop' && shown(p)) },
    { title: 'Office', pages: PHONE_PAGES.filter((p) => p.group === 'office' && shown(p)) },
  ];
}
